import { z } from "zod";

/**
 * LES CONNEXIONS : un acces a un service exterieur (une API par jeton, une
 * boite mail, un agenda), gardees UNE FOIS dans le socle et servies a tous ses
 * consommateurs : une etape HTTP d'une automatisation (Flux), Lia, le MCP.
 * Doctrine : `docs/socle/connexions.md`.
 *
 * UN SEUL MECANISME, TROIS PIECES :
 *   - le REGISTRE des fournisseurs (`FOURNISSEURS_CONNEXION`, ci-dessous) : qui
 *     existe, comment il s'authentifie, quels services il rend. Un fournisseur
 *     nouveau est une entree de plus, jamais une collection de plus ;
 *   - le document PUBLIC `orgs/{orgId}/connexions/{id}` (forme `Connexion`) :
 *     nom, fournisseur, portee, niveau d'acces, apercu, statut. Jamais le secret ;
 *   - le SECRET `orgs/{orgId}/connexionsSecrets/{id}`, lu par le serveur SEUL
 *     (les rules le ferment a tout client, owner compris).
 *
 * LA PORTEE : `perso`, la connexion de son auteur seul ; `org`, celle de tout
 * membre actif. LE NIVEAU D'ACCES borne ce qu'un consommateur peut en faire.
 * Chaque fournisseur DECLARE ses niveaux, du plus faible au plus fort : un
 * secret a `lecture` (appeler une API en GET) et `ecriture` ; Gmail a
 * `lecture`, `brouillons`, `envoi` ; un agenda `disponibilites`, `lecture`,
 * `ecriture`. Un niveau couvre ceux qui le precedent. Le serveur le verifie a
 * chaque usage (`resoudreConnexion`, `functions/src/connexions/`).
 *
 * Ce fichier est bundle vers les Functions (`functions/build-studio.mjs`) :
 * `functions/` n'importe aucun `@taki/*`.
 */

export const CONNEXION_SCHEMA_VERSION = 1 as const;

/** Partagee a l'org, ou personnelle a son auteur (les deux projets du rail). */
export const PORTEES_CONNEXION = ["org", "perso"] as const;
export type PorteeConnexion = (typeof PORTEES_CONNEXION)[number];

/** Les niveaux d'un fournisseur a SECRET (et ceux que demande une etape HTTP de Flux) ; `ecriture` comprend `lecture`. */
export const NIVEAUX_ACCES_CONNEXION = ["lecture", "ecriture"] as const;
/** Tous les niveaux connus du registre ; chaque fournisseur declare les siens, dans l'ordre. */
export const IDS_NIVEAUX_ACCES_CONNEXION = ["disponibilites", "lecture", "brouillons", "ecriture", "envoi"] as const;
export type NiveauAccesConnexion = (typeof IDS_NIVEAUX_ACCES_CONNEXION)[number];

/** Un niveau d'acces d'un fournisseur : son libelle, une ligne d'aide, et pour OAuth les scopes qu'il demande. */
export interface NiveauAccesFournisseur {
  id: NiveauAccesConnexion;
  libelle: string;
  detail: string;
  /** OAuth : les scopes de CE niveau, en plus des scopes communs du fournisseur. */
  scopes?: readonly string[];
}

/** Qui se sert d'une connexion OAuth : Lia (dans l'app, sur WhatsApp, et l'assistant MCP du membre), ou aussi ses automatisations. */
export const USAGES_CONNEXION = ["lia", "lia_flux"] as const;
export type UsageConnexion = (typeof USAGES_CONNEXION)[number];
export const LIBELLES_USAGE_CONNEXION: Readonly<Record<UsageConnexion, string>> = { lia: "Lia", lia_flux: "Lia et Flux" };

/** `a_renouveler` : un jeton OAuth expire ou revoque ; `erreur` : le dernier usage a echoue. */
export const STATUTS_CONNEXION = ["active", "a_renouveler", "erreur"] as const;
export type StatutConnexion = (typeof STATUTS_CONNEXION)[number];

/**
 * Les services qu'un fournisseur rend. `http` : signer une requete HTTP ;
 * `connecteur` : servir l'authentification d'une piece Activepieces (Google
 * Sheets, Slack, Stripe...) a une etape ou un declencheur de Flux.
 */
export const SERVICES_CONNEXION = ["http", "mail", "agenda", "connecteur"] as const;
export type ServiceConnexion = (typeof SERVICES_CONNEXION)[number];

/** `secret` : saisi une fois, garde par le serveur. `oauth2` : consentement chez le fournisseur. */
export const AUTHENTIFICATIONS_CONNEXION = ["secret", "oauth2"] as const;
export type AuthentificationConnexion = (typeof AUTHENTIFICATIONS_CONNEXION)[number];

/** Comment un secret signe une requete HTTP. */
export const FORMES_SECRET_CONNEXION = ["jeton", "cle", "identifiant", "entetes"] as const;
export type FormeSecretConnexion = (typeof FORMES_SECRET_CONNEXION)[number];

export const IDS_FOURNISSEURS_CONNEXION = [
  "jeton", "cle_api", "basique", "entetes",
  "google_agenda", "gmail", "outlook_agenda", "outlook_mail",
  "google_sheets", "google_drive", "microsoft_teams", "microsoft_excel", "slack", "notion", "hubspot", "typeform", "stripe", "brevo",
] as const;
export type IdFournisseurConnexion = (typeof IDS_FOURNISSEURS_CONNEXION)[number];

/**
 * Les EDITEURS OAuth : une application OAuth de Taki chacun, par environnement.
 * Google et Microsoft ont leurs adaptateurs (mail, agenda) ; les autres passent
 * par l'editeur GENERIQUE des Functions, pilote par une table
 * (`functions/src/connexions/fournisseurs/generique.ts`).
 */
export const EDITEURS_OAUTH_CONNEXION = ["google", "microsoft", "slack", "notion", "hubspot", "typeform"] as const;
export type EditeurOAuthConnexion = (typeof EDITEURS_OAUTH_CONNEXION)[number];

/**
 * Un scope OAuth qui commence par ce prefixe est demande AU NOM DE LA PERSONNE,
 * pas de l'application : Slack separe `scope` (le robot) et `user_scope` (le
 * membre, pour chercher ou parler en son nom). Le prefixe vit dans le registre,
 * l'editeur generique le retire en construisant l'adresse du consentement.
 */
export const PREFIXE_SCOPE_UTILISATEUR = "user_scope:";

export interface FournisseurConnexion {
  id: IdFournisseurConnexion;
  /** Libelle court, affiche dans la liste et le choix du fournisseur. */
  libelle: string;
  /** Une ligne. */
  description: string;
  authentification: AuthentificationConnexion;
  services: readonly ServiceConnexion[];
  /** `false` : visible, grise, avec sa `raison`. */
  disponible: boolean;
  raison?: string;
  /** Pour `secret` : la forme attendue et un exemple a montrer en indication. */
  secret?: { forme: FormeSecretConnexion; exemple: string; aide: string };
  /** Les niveaux d'acces, du plus faible au plus fort ; le premier sert par defaut sauf `accesParDefaut`. */
  niveaux: readonly NiveauAccesFournisseur[];
  accesParDefaut: NiveauAccesConnexion;
  /**
   * Pour `oauth2` : l'editeur (son application OAuth et ses adaptateurs,
   * `functions/src/connexions/fournisseurs/`), les scopes de toute connexion
   * (reconnaitre le compte, garder l'acces), et le libelle de la selection
   * (agendas, libelles ou dossiers inclus) quand le service en a une, et
   * l'usage pose a la creation (defaut `lia`).
   */
  oauth?: { editeur: EditeurOAuthConnexion; scopesCommuns: readonly string[]; selection?: string; usageParDefaut?: UsageConnexion };
}

/** Les niveaux de tout fournisseur a secret. */
const NIVEAUX_SECRET: readonly NiveauAccesFournisseur[] = [
  { id: "lecture", libelle: "Lecture", detail: "Requêtes GET seulement" },
  { id: "ecriture", libelle: "Écriture", detail: "Toutes les requêtes" },
];

/** Les niveaux d'un CONNECTEUR a cle (Stripe, Brevo) : l'action de la piece est classee lecture ou ecriture. */
const NIVEAUX_CLE_CONNECTEUR: readonly NiveauAccesFournisseur[] = [
  { id: "lecture", libelle: "Lecture", detail: "Consulter seulement" },
  { id: "ecriture", libelle: "Lecture et écriture", detail: "Créer, modifier, supprimer" },
];

const G = "https://www.googleapis.com/auth/";
const COMMUNS_GOOGLE = ["openid", "email"] as const;
const COMMUNS_MICROSOFT = ["openid", "email", "offline_access", "User.Read"] as const;
const U = PREFIXE_SCOPE_UTILISATEUR;

/** Slack, en lecture : le robot lit canaux, messages, membres ; la personne cherche. */
const SLACK_LECTURE = [
  "channels:read", "channels:history", "groups:read", "groups:history", "im:read", "im:history", "mpim:read", "mpim:history",
  "users:read", "users:read.email", "users.profile:read", "reactions:read", "files:read", "usergroups:read", "emoji:read",
  `${U}search:read`,
] as const;
/** Slack, en ecriture : en plus, le robot ecrit et gere les canaux ; la personne parle et regle son statut. */
const SLACK_ECRITURE = [
  ...SLACK_LECTURE,
  "chat:write", "chat:write.customize", "channels:manage", "channels:join", "channels:write.invites", "groups:write", "groups:write.invites",
  "im:write", "mpim:write", "reactions:write", "files:write", "usergroups:write",
  `${U}chat:write`, `${U}reactions:write`, `${U}users.profile:write`, `${U}usergroups:write`, `${U}channels:write`,
] as const;
const HUBSPOT_LECTURE = [
  "crm.objects.contacts.read", "crm.objects.companies.read", "crm.objects.deals.read", "crm.objects.owners.read",
  "crm.schemas.contacts.read", "crm.schemas.companies.read", "crm.schemas.deals.read", "crm.lists.read", "tickets",
] as const;
const TYPEFORM_LECTURE = ["forms:read", "responses:read", "workspaces:read", "themes:read", "images:read", "webhooks:read", "webhooks:write"] as const;

/**
 * LE REGISTRE. Les quatre premiers signent une requete HTTP par un secret ;
 * les quatre suivants (Google Agenda, Gmail, Outlook Agenda, Outlook Mail,
 * chantier `connecteurs-lia`) se relient par OAuth 2, un service chacun, avec
 * leurs niveaux et les scopes minimaux de chaque niveau. Les dix derniers
 * servent les CONNECTEURS de Flux (pieces Activepieces, service `connecteur`) :
 * huit par OAuth 2, Stripe et Brevo par une cle. Un fournisseur OAuth dont
 * l'application n'est pas configuree dans l'environnement est grise par le
 * serveur (`connexionsApi`, operation `fournisseurs`).
 */
export const FOURNISSEURS_CONNEXION: readonly FournisseurConnexion[] = [
  {
    id: "jeton",
    libelle: "Jeton Bearer",
    description: "Un jeton envoyé dans l'en-tête Authorization.",
    authentification: "secret",
    services: ["http"],
    disponible: true,
    niveaux: NIVEAUX_SECRET,
    accesParDefaut: "lecture",
    secret: { forme: "jeton", exemple: "eyJhbGciOi…", aide: "Le jeton seul, sans « Bearer »." },
  },
  {
    id: "cle_api",
    libelle: "Clé d'API",
    description: "Une clé envoyée dans l'en-tête de votre choix.",
    authentification: "secret",
    services: ["http"],
    disponible: true,
    niveaux: NIVEAUX_SECRET,
    accesParDefaut: "lecture",
    secret: { forme: "cle", exemple: "sk_live_…", aide: "La clé seule." },
  },
  {
    id: "basique",
    libelle: "Basique",
    description: "Un identifiant et un mot de passe (HTTP Basic).",
    authentification: "secret",
    services: ["http"],
    disponible: true,
    niveaux: NIVEAUX_SECRET,
    accesParDefaut: "lecture",
    secret: { forme: "identifiant", exemple: "utilisateur:motdepasse", aide: "Identifiant et mot de passe, séparés par deux-points." },
  },
  {
    id: "entetes",
    libelle: "En-têtes",
    description: "Des en-têtes au choix, en objet JSON.",
    authentification: "secret",
    services: ["http"],
    disponible: true,
    niveaux: NIVEAUX_SECRET,
    accesParDefaut: "lecture",
    secret: { forme: "entetes", exemple: '{"X-Token": "…"}', aide: "Un objet JSON : nom d'en-tête, valeur." },
  },
  {
    id: "google_agenda",
    libelle: "Google Agenda",
    description: "Votre agenda Google, pour Lia et vos automatisations.",
    authentification: "oauth2",
    services: ["agenda"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "google", scopesCommuns: COMMUNS_GOOGLE, selection: "Agendas" },
    niveaux: [
      { id: "disponibilites", libelle: "Disponibilités", detail: "Libre ou occupé, sans détail", scopes: [`${G}calendar.freebusy`, `${G}calendar.calendarlist.readonly`] },
      { id: "lecture", libelle: "Lecture", detail: "Voir les événements", scopes: [`${G}calendar.events.readonly`, `${G}calendar.calendarlist.readonly`] },
      { id: "ecriture", libelle: "Lecture et écriture", detail: "Créer, modifier, supprimer", scopes: [`${G}calendar.events`, `${G}calendar.calendarlist.readonly`] },
    ],
  },
  {
    id: "gmail",
    libelle: "Gmail",
    description: "Votre messagerie Gmail, pour Lia et vos automatisations.",
    authentification: "oauth2",
    services: ["mail"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "google", scopesCommuns: COMMUNS_GOOGLE, selection: "Libellés" },
    niveaux: [
      { id: "lecture", libelle: "Lecture", detail: "Chercher et lire", scopes: [`${G}gmail.readonly`] },
      // Gmail ne separe pas brouillon et envoi (`gmail.compose` fait les deux) : le serveur tient la limite.
      { id: "brouillons", libelle: "Lecture et brouillons", detail: "Lia prépare, vous envoyez", scopes: [`${G}gmail.readonly`, `${G}gmail.compose`] },
      { id: "envoi", libelle: "Envoi", detail: "Lia envoie après votre accord", scopes: [`${G}gmail.readonly`, `${G}gmail.compose`] },
    ],
  },
  {
    id: "outlook_agenda",
    libelle: "Outlook Agenda",
    description: "Votre agenda Microsoft 365 ou Outlook.com.",
    authentification: "oauth2",
    services: ["agenda"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "microsoft", scopesCommuns: COMMUNS_MICROSOFT, selection: "Agendas" },
    niveaux: [
      { id: "disponibilites", libelle: "Disponibilités", detail: "Libre ou occupé, sans détail", scopes: ["Calendars.ReadBasic"] },
      { id: "lecture", libelle: "Lecture", detail: "Voir les événements", scopes: ["Calendars.Read"] },
      { id: "ecriture", libelle: "Lecture et écriture", detail: "Créer, modifier, supprimer", scopes: ["Calendars.ReadWrite"] },
    ],
  },
  {
    id: "outlook_mail",
    libelle: "Outlook Mail",
    description: "Votre messagerie Microsoft 365 ou Outlook.com.",
    authentification: "oauth2",
    services: ["mail"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "microsoft", scopesCommuns: COMMUNS_MICROSOFT, selection: "Dossiers" },
    niveaux: [
      { id: "lecture", libelle: "Lecture", detail: "Chercher et lire", scopes: ["Mail.Read"] },
      { id: "brouillons", libelle: "Lecture et brouillons", detail: "Lia prépare, vous envoyez", scopes: ["Mail.ReadWrite"] },
      { id: "envoi", libelle: "Envoi", detail: "Lia envoie après votre accord", scopes: ["Mail.ReadWrite", "Mail.Send"] },
    ],
  },
  // --- Les connecteurs de Flux (pieces Activepieces), service `connecteur`. Les scopes de chaque
  // niveau sont ceux qu'il faut aux actions et declencheurs de la piece de ce niveau.
  {
    id: "google_sheets",
    libelle: "Google Sheets",
    description: "Vos feuilles de calcul Google, pour vos automatisations.",
    authentification: "oauth2",
    services: ["connecteur"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "google", scopesCommuns: COMMUNS_GOOGLE, usageParDefaut: "lia_flux" },
    niveaux: [
      // `drive.readonly` : lister les feuilles (menus du builder) et surveiller leurs changements.
      { id: "lecture", libelle: "Lecture", detail: "Lire les feuilles", scopes: [`${G}spreadsheets.readonly`, `${G}drive.readonly`] },
      { id: "ecriture", libelle: "Lecture et écriture", detail: "Ajouter, modifier, effacer des lignes", scopes: [`${G}spreadsheets`, `${G}drive.readonly`, `${G}drive.file`] },
    ],
  },
  {
    id: "google_drive",
    libelle: "Google Drive",
    description: "Vos fichiers et dossiers Google Drive.",
    authentification: "oauth2",
    services: ["connecteur"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "google", scopesCommuns: COMMUNS_GOOGLE, usageParDefaut: "lia_flux" },
    niveaux: [
      { id: "lecture", libelle: "Lecture", detail: "Chercher et lire les fichiers", scopes: [`${G}drive.readonly`] },
      // Deplacer, partager ou supprimer un fichier que MyTaki n'a pas cree demande `drive` entier.
      { id: "ecriture", libelle: "Lecture et écriture", detail: "Créer, déplacer, partager, supprimer", scopes: [`${G}drive`] },
    ],
  },
  {
    id: "microsoft_teams",
    libelle: "Microsoft Teams",
    description: "Vos équipes, canaux et conversations Teams.",
    authentification: "oauth2",
    services: ["connecteur"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "microsoft", scopesCommuns: COMMUNS_MICROSOFT, usageParDefaut: "lia_flux" },
    niveaux: [
      {
        id: "lecture", libelle: "Lecture", detail: "Lire équipes, canaux et messages",
        scopes: ["Team.ReadBasic.All", "Channel.ReadBasic.All", "ChannelMessage.Read.All", "Chat.Read", "TeamMember.Read.All", "User.ReadBasic.All"],
      },
      {
        id: "ecriture", libelle: "Lecture et écriture", detail: "Envoyer des messages, créer des canaux",
        scopes: ["Team.ReadBasic.All", "Channel.ReadBasic.All", "ChannelMessage.Read.All", "Chat.ReadWrite", "TeamMember.Read.All", "User.ReadBasic.All", "ChannelMessage.Send", "Channel.Create"],
      },
    ],
  },
  {
    id: "microsoft_excel",
    libelle: "Microsoft Excel",
    description: "Vos classeurs Excel, sur OneDrive ou SharePoint.",
    authentification: "oauth2",
    services: ["connecteur"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "microsoft", scopesCommuns: COMMUNS_MICROSOFT, usageParDefaut: "lia_flux" },
    niveaux: [
      { id: "lecture", libelle: "Lecture", detail: "Lire les classeurs", scopes: ["Files.Read", "Sites.Read.All"] },
      { id: "ecriture", libelle: "Lecture et écriture", detail: "Ajouter, modifier, effacer", scopes: ["Files.ReadWrite", "Sites.ReadWrite.All"] },
    ],
  },
  {
    id: "slack",
    libelle: "Slack",
    description: "Votre espace de travail Slack.",
    authentification: "oauth2",
    services: ["connecteur"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "slack", scopesCommuns: [], usageParDefaut: "lia_flux" },
    niveaux: [
      { id: "lecture", libelle: "Lecture", detail: "Lire canaux, messages et membres", scopes: SLACK_LECTURE },
      { id: "ecriture", libelle: "Lecture et écriture", detail: "Envoyer des messages, gérer les canaux", scopes: SLACK_ECRITURE },
    ],
  },
  {
    id: "notion",
    libelle: "Notion",
    description: "Les pages et bases Notion que vous partagez avec MyTaki.",
    authentification: "oauth2",
    services: ["connecteur"],
    disponible: true,
    accesParDefaut: "lecture",
    // Notion n'a pas de scopes : les droits sont ceux de l'integration, le serveur borne le niveau.
    oauth: { editeur: "notion", scopesCommuns: [], usageParDefaut: "lia_flux" },
    niveaux: [
      { id: "lecture", libelle: "Lecture", detail: "Lire pages et bases", scopes: [] },
      { id: "ecriture", libelle: "Lecture et écriture", detail: "Créer et modifier pages et bases", scopes: [] },
    ],
  },
  {
    id: "hubspot",
    libelle: "HubSpot",
    description: "Contacts, entreprises, transactions et tickets HubSpot.",
    authentification: "oauth2",
    services: ["connecteur"],
    disponible: true,
    accesParDefaut: "lecture",
    oauth: { editeur: "hubspot", scopesCommuns: ["oauth"], usageParDefaut: "lia_flux" },
    niveaux: [
      { id: "lecture", libelle: "Lecture", detail: "Lire le CRM", scopes: HUBSPOT_LECTURE },
      {
        id: "ecriture", libelle: "Lecture et écriture", detail: "Créer et modifier dans le CRM",
        scopes: [...HUBSPOT_LECTURE, "crm.objects.contacts.write", "crm.objects.companies.write", "crm.objects.deals.write", "crm.lists.write"],
      },
    ],
  },
  {
    id: "typeform",
    libelle: "Typeform",
    description: "Vos formulaires Typeform et leurs réponses.",
    authentification: "oauth2",
    services: ["connecteur"],
    disponible: true,
    accesParDefaut: "lecture",
    // `offline` : un jeton de rafraichissement. Les webhooks servent les declencheurs (nouvelle reponse), des la lecture.
    oauth: { editeur: "typeform", scopesCommuns: ["offline", "accounts:read"], usageParDefaut: "lia_flux" },
    niveaux: [
      { id: "lecture", libelle: "Lecture", detail: "Lire formulaires et réponses", scopes: TYPEFORM_LECTURE },
      {
        id: "ecriture", libelle: "Lecture et écriture", detail: "Créer et modifier des formulaires",
        scopes: [...TYPEFORM_LECTURE, "forms:write", "responses:write", "workspaces:write", "themes:write", "images:write"],
      },
    ],
  },
  {
    id: "stripe",
    libelle: "Stripe",
    description: "Paiements, clients et factures Stripe, par clé secrète.",
    authentification: "secret",
    services: ["connecteur"],
    disponible: true,
    niveaux: NIVEAUX_CLE_CONNECTEUR,
    accesParDefaut: "lecture",
    secret: { forme: "cle", exemple: "sk_live_…", aide: "Stripe > Développeurs > Clés API : la clé secrète, ou une clé restreinte." },
  },
  {
    id: "brevo",
    libelle: "Brevo",
    description: "Contacts, listes et e-mails Brevo, par clé d'API.",
    authentification: "secret",
    services: ["connecteur"],
    disponible: true,
    niveaux: NIVEAUX_CLE_CONNECTEUR,
    accesParDefaut: "lecture",
    secret: { forme: "cle", exemple: "xkeysib-…", aide: "Brevo > Paramètres > SMTP et API > Clés API." },
  },
];

export function fournisseurConnexion(id: unknown): FournisseurConnexion | undefined {
  return FOURNISSEURS_CONNEXION.find((f) => f.id === id);
}

/** Les niveaux d'un fournisseur, dans l'ordre (ceux d'un secret si le fournisseur est inconnu). */
export function niveauxDeConnexion(fournisseur?: unknown): readonly NiveauAccesConnexion[] {
  return (fournisseurConnexion(fournisseur)?.niveaux ?? NIVEAUX_SECRET).map((n) => n.id);
}

/**
 * Vrai si `accorde` couvre `requis` POUR CE FOURNISSEUR : un niveau couvre ceux
 * qui le precedent dans sa liste (`ecriture` comprend `lecture`, `envoi`
 * comprend `brouillons`). Un niveau inconnu du fournisseur ne suffit jamais.
 */
export function accesSuffit(accorde: unknown, requis: NiveauAccesConnexion, fournisseur?: unknown): boolean {
  const ordre: readonly string[] = niveauxDeConnexion(fournisseur);
  const i = ordre.indexOf(String(accorde));
  const j = ordre.indexOf(requis);
  return i >= 0 && j >= 0 && i >= j;
}

/** OAuth : les scopes de ce niveau (communs compris), sans doublon. */
export function scopesDuNiveau(fournisseur: unknown, acces: unknown): string[] {
  const f = fournisseurConnexion(fournisseur);
  const niveau = f?.niveaux.find((n) => n.id === acces);
  if (!f?.oauth || !niveau) throw new Error(`Niveau ${String(acces)} inconnu pour ${String(fournisseur)}.`);
  return [...new Set([...f.oauth.scopesCommuns, ...(niveau.scopes ?? [])])];
}

/** OAuth : les scopes du niveau que la connexion n'a pas encore ; vide = pas de nouveau consentement. */
export function scopesManquants(fournisseur: unknown, acces: unknown, accordes: readonly string[]): string[] {
  const deja = new Set(accordes);
  return scopesDuNiveau(fournisseur, acces).filter((s) => !deja.has(s) && !["openid", "email", "offline_access"].includes(s));
}

/**
 * Le document public `orgs/{orgId}/connexions/{id}` (dates en Timestamp dans la
 * base, en `Date` cote client). Le secret n'y est JAMAIS.
 */
export interface Connexion {
  id: string;
  orgId: string;
  nom: string;
  fournisseur: IdFournisseurConnexion;
  portee: PorteeConnexion;
  acces: NiveauAccesConnexion;
  /** Pour `cle_api` : l'en-tete qui porte la cle. */
  entete?: string;
  /** Les quatre derniers caracteres du secret, pour le reconnaitre sans le lire ; vide en OAuth. */
  apercu: string;
  statut: StatutConnexion;
  /** OAuth : le compte relie (l'adresse affichee), connu apres le consentement. */
  compte?: string;
  /** OAuth : les autorisations accordees par le fournisseur. */
  scopes?: string[];
  /** OAuth : l'expiration du jeton d'acces (le jeton de rafraichissement reste au serveur). */
  expireAt?: Date | null;
  /** Le dernier refus du fournisseur, lisible, quand `statut` vaut `erreur` ou `a_renouveler`. */
  erreur?: string;
  /** OAuth : qui s'en sert (Lia seule, ou Lia et Flux). */
  usage?: UsageConnexion;
  /** OAuth : agendas, libelles ou dossiers inclus ; vide = le principal (agenda) ou tous (mail). */
  selection?: { id: string; nom: string }[];
  /** OAuth : le dernier usage par Lia, le MCP ou Flux. */
  derniereUtilisation?: Date | null;
  auteurUid: string;
  createdAt: Date;
  updatedAt: Date;
}

const ENTETE = /^[A-Za-z0-9-]{1,64}$/;
export const SECRET_CONNEXION_MAX = 8000;

/**
 * Le secret a la forme que son fournisseur attend ? Rend le message du refus,
 * ou `null`. Partage par la saisie (ecran, serveur) et `entetesDeConnexion`.
 */
export function problemeSecretConnexion(fournisseur: IdFournisseurConnexion, secret: string): string | null {
  const forme = fournisseurConnexion(fournisseur)?.secret?.forme;
  if (forme === undefined) return "Ce fournisseur ne prend pas de secret saisi.";
  if (secret.length === 0) return "Secret requis.";
  if (secret.length > SECRET_CONNEXION_MAX) return "Secret trop long.";
  if (forme === "identifiant" && !secret.includes(":")) return "Identifiant et mot de passe, séparés par deux-points.";
  if (forme === "entetes") {
    let lu: unknown;
    try {
      lu = JSON.parse(secret);
    } catch {
      return "Les en-têtes doivent être un objet JSON.";
    }
    if (lu === null || typeof lu !== "object" || Array.isArray(lu)) return "Les en-têtes doivent être un objet JSON.";
    const cles = Object.keys(lu);
    if (cles.length === 0) return "Au moins un en-tête.";
    if (cles.some((c) => !ENTETE.test(c))) return "Nom d'en-tête invalide (lettres, chiffres, tirets).";
  }
  return null;
}

/**
 * LA SAISIE d'une connexion a secret. Les fournisseurs OAuth ne passent pas
 * par ici : leur secret vient du consentement, jamais d'un champ.
 */
export const ConnexionSaisieSchema = z
  .object({
    nom: z.string().trim().min(1, "Nom requis.").max(80, "Nom trop long (80 caracteres)."),
    fournisseur: z.enum(IDS_FOURNISSEURS_CONNEXION),
    portee: z.enum(PORTEES_CONNEXION).default("perso"),
    acces: z.enum(NIVEAUX_ACCES_CONNEXION).default("lecture"),
    entete: z.string().trim().regex(ENTETE, "En-tête invalide (lettres, chiffres, tirets).").optional(),
    secret: z.string().max(SECRET_CONNEXION_MAX).optional(),
  })
  .strict()
  .superRefine((saisie, ctx) => {
    const fournisseur = fournisseurConnexion(saisie.fournisseur);
    if (!fournisseur?.disponible) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fournisseur"], message: `${fournisseur?.libelle ?? saisie.fournisseur} : ${fournisseur?.raison ?? "indisponible"}.` });
      return;
    }
    if (fournisseur.authentification !== "secret") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fournisseur"], message: "Ce fournisseur se relie par son consentement, pas par un secret." });
      return;
    }
    const probleme = problemeSecretConnexion(saisie.fournisseur, saisie.secret ?? "");
    if (probleme !== null) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["secret"], message: probleme });
    if (saisie.fournisseur === "cle_api" && !saisie.entete) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["entete"], message: "Une clé d'API demande le nom de son en-tête." });
    if (saisie.fournisseur !== "cle_api" && saisie.entete) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["entete"], message: "Seule une clé d'API porte un en-tête." });
  });
export type ConnexionSaisie = z.infer<typeof ConnexionSaisieSchema>;

/** « ••••abcd » : les quatre derniers caracteres, rien de plus (rien du tout sous huit caracteres). */
export function apercuDeSecret(valeur: string): string {
  return valeur.length >= 8 ? `••••${valeur.slice(-4)}` : "••••";
}

/**
 * CE QU'UN SECRET POSE SUR UNE REQUETE HTTP : les en-tetes, et toutes les
 * valeurs a masquer dans une trace (le secret, et ce qui en derive). Leve une
 * `Error` au message lisible si le secret n'a pas sa forme.
 */
export function entetesDeConnexion(
  fournisseur: IdFournisseurConnexion,
  secret: string,
  entete?: string,
): { entetes: Record<string, string>; masques: string[] } {
  const probleme = problemeSecretConnexion(fournisseur, secret);
  if (probleme !== null) throw new Error(probleme);
  switch (fournisseurConnexion(fournisseur)?.secret?.forme) {
    case "jeton":
      return { entetes: { Authorization: `Bearer ${secret}` }, masques: [secret] };
    case "cle": {
      const nom = entete && ENTETE.test(entete) ? entete : "X-API-Key";
      return { entetes: { [nom]: secret }, masques: [secret] };
    }
    case "identifiant": {
      const encode = btoa(String.fromCharCode(...new TextEncoder().encode(secret)));
      const motDePasse = secret.slice(secret.indexOf(":") + 1);
      return { entetes: { Authorization: `Basic ${encode}` }, masques: [secret, encode, ...(motDePasse ? [motDePasse] : [])] };
    }
    case "entetes": {
      const entetes = Object.fromEntries(Object.entries(JSON.parse(secret) as Record<string, unknown>).map(([k, v]) => [k, String(v)]));
      return { entetes, masques: [secret, ...Object.values(entetes).filter((v) => v.length > 0)] };
    }
    default:
      throw new Error("Ce fournisseur ne signe pas de requête HTTP.");
  }
}

/**
 * LES REGLAGES d'une connexion OAuth, tels que son membre les change
 * (Parametres > Conversations, ou l'Atelier) : nom affiche, niveau d'acces,
 * selection, usage. Le niveau est verifie contre ceux du fournisseur par le
 * serveur ; monter au-dela des scopes accordes redemande le consentement.
 */
export const ConnexionReglagesSchema = z
  .object({
    nom: z.string().trim().min(1).max(80).optional(),
    acces: z.enum(IDS_NIVEAUX_ACCES_CONNEXION).optional(),
    selection: z.array(z.string().trim().min(1).max(512)).max(50).optional(),
    usage: z.enum(USAGES_CONNEXION).optional(),
  })
  .strict();
export type ConnexionReglages = z.infer<typeof ConnexionReglagesSchema>;

/**
 * LE RETOUR D'UN CONSENTEMENT OAUTH. Apres l'ecran du fournisseur, le serveur
 * renvoie le navigateur sur Parametres > Conversations, ou sur les Connexions
 * de Flux si le geste est parti de la (`functions/src/connexions/oauth.ts`,
 * `adresseApresRetour`), avec `connecteur=ok|refuse|erreur` et parfois
 * `detail=maj|droits|non_configure`. Rend la ligne a dire, ou `null` si
 * l'adresse n'est pas un retour.
 */
export const PARAMETRES_RETOUR_CONNEXION = ["connecteur", "detail"] as const;

export function messageDuRetourConnexion(search: string): { tone: "status" | "error"; texte: string } | null {
  const parametres = new URLSearchParams(search);
  const issue = parametres.get("connecteur");
  if (issue === null || issue === "") return null;
  const detail = parametres.get("detail");
  if (issue === "ok") return { tone: "status", texte: detail === "maj" ? "Connexion mise à jour." : "Connexion ajoutée." };
  if (detail === "droits") return { tone: "error", texte: "Droits refusés chez le fournisseur." };
  if (detail === "non_configure") return { tone: "error", texte: "Fournisseur non configuré." };
  if (issue === "refuse") return { tone: "status", texte: "Connexion annulée." };
  return { tone: "error", texte: "Connexion impossible." };
}
